/**
 * TemplatesSidebar Component
 *
 * Sidebar navigation for templates page showing:
 * - User's template list with drag-and-drop reordering for pinned templates
 * - Actions to create new templates
 * - Link to template builder
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  DragOverlay,
  type DragStartEvent,
  MeasuringStrategy,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { EntitlementDisabledTooltip, EntitlementUsage } from '@features/entitlements';
import {
  Add as AddIcon,
  Business as OrgIcon,
  CloudUpload as UploadIcon,
  LibraryBooks as LibraryIcon,
} from '@mui/icons-material';
import { Box, Button, Divider, Paper, Skeleton, Typography } from '@mui/material';

import { LAYOUT_DIMENSIONS } from '../constants/layout';
import type { TemplateOrderItem, TemplateWithMeta } from '../types';

import { TemplateListItem } from './TemplateListItem';

interface TemplatesSidebarProps {
  /** List of templates */
  templates: TemplateWithMeta[];
  /** Currently selected template ID */
  selectedTemplateId: string | null;
  /** Whether templates are loading */
  isLoading: boolean;
  /** Callback when template is selected */
  onSelectTemplate: (template: TemplateWithMeta) => void;
  /** Callback to create template from scratch */
  onCreateFromScratch: () => void;
  /** Callback to browse library */
  onBrowseLibrary: () => void;
  /** Callback to reorder templates */
  onReorderTemplates: (templateOrders: TemplateOrderItem[]) => Promise<boolean>;
  /** Callback to pin a template */
  onPinTemplate: (templateId: string, position: number) => Promise<boolean>;
  /** Callback to unpin a template */
  onUnpinTemplate: (templateId: string) => Promise<boolean>;
  /** Whether user is org admin */
  isOrgAdmin?: boolean;
  /** Callback to view organization templates */
  onViewOrganizationTemplates?: () => void;
}

export const TemplatesSidebar: React.FC<TemplatesSidebarProps> = ({
  templates,
  selectedTemplateId,
  isLoading,
  onSelectTemplate,
  onCreateFromScratch,
  onBrowseLibrary,
  onReorderTemplates,
  onPinTemplate,
  onUnpinTemplate,
  isOrgAdmin,
  onViewOrganizationTemplates,
}) => {
  const navigate = useNavigate();
  const templatesListRef = useRef<HTMLDivElement>(null);
  const [showBottomGradient, setShowBottomGradient] = useState(false);
  const [activeId, setActiveId] = useState<string | null>(null);

  // Configure drag sensors - require 8px drag before activating
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
  );

  // Separate pinned and unpinned templates
  const { pinnedTemplates, unpinnedTemplates } = useMemo(() => {
    const pinned: TemplateWithMeta[] = [];
    const unpinned: TemplateWithMeta[] = [];

    templates.forEach(template => {
      if (template.displayOrder !== null) {
        pinned.push(template);
      } else {
        unpinned.push(template);
      }
    });

    // Sort pinned templates by display_order
    pinned.sort((a, b) => {
      const aOrder = a.displayOrder ?? 999;
      const bOrder = b.displayOrder ?? 999;
      return aOrder - bOrder;
    });

    return { pinnedTemplates: pinned, unpinnedTemplates: unpinned };
  }, [templates]);

  // Check if there's more content to scroll
  const checkScrollOverflow = useCallback(() => {
    if (!templatesListRef.current) return;
    const element = templatesListRef.current;
    const hasOverflow = element.scrollHeight > element.clientHeight;
    const isScrolledToBottom = element.scrollHeight - element.scrollTop - element.clientHeight < 5;
    setShowBottomGradient(hasOverflow && !isScrolledToBottom);
  }, []);

  useEffect(() => {
    const element = templatesListRef.current;
    if (!element) return;

    // Check on scroll
    const handleScroll = () => {
      checkScrollOverflow();
    };

    element.addEventListener('scroll', handleScroll, { passive: true });

    // Check on resize
    const resizeObserver = new ResizeObserver(() => {
      checkScrollOverflow();
    });

    resizeObserver.observe(element);

    // Initial check - use requestAnimationFrame to ensure DOM is fully rendered
    const rafId = requestAnimationFrame(() => {
      checkScrollOverflow();
      // Also check again after a short delay to catch any async rendering
      setTimeout(checkScrollOverflow, 100);
    });

    return () => {
      cancelAnimationFrame(rafId);
      element.removeEventListener('scroll', handleScroll);
      resizeObserver.disconnect();
    };
  }, [checkScrollOverflow, templates, isLoading]);

  const handleUploadClick = () => {
    navigate('/templates/build');
  };

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveId(null);

    if (!over || active.id === over.id) return;

    // Find the templates being moved
    const activeTemplate = pinnedTemplates.find(t => t.template.id === active.id);
    const overTemplate = pinnedTemplates.find(t => t.template.id === over.id);

    if (!activeTemplate || !overTemplate) return;

    const oldIndex = pinnedTemplates.findIndex(t => t.template.id === active.id);
    const newIndex = pinnedTemplates.findIndex(t => t.template.id === over.id);

    if (oldIndex === -1 || newIndex === -1) return;

    // Build template orders for all pinned templates
    const templateOrders: TemplateOrderItem[] = [];

    // Create new order array
    const reorderedPinned = [...pinnedTemplates];
    const [movedItem] = reorderedPinned.splice(oldIndex, 1);
    reorderedPinned.splice(newIndex, 0, movedItem);

    // Update display_order for all pinned templates
    reorderedPinned.forEach((template, index) => {
      templateOrders.push({
        template_id: template.template.id,
        display_order: index,
      });
    });

    // Call reorder API
    await onReorderTemplates(templateOrders);
  };

  const handlePinClick = async (e: React.MouseEvent, template: TemplateWithMeta) => {
    e.stopPropagation();
    if (template.displayOrder === null) {
      // Pin to the end of pinned list
      const nextPosition = Math.min(pinnedTemplates.length, 4);
      await onPinTemplate(template.template.id, nextPosition);
    } else {
      // Unpin
      await onUnpinTemplate(template.template.id);
    }
  };

  const activeTemplate = activeId ? pinnedTemplates.find(t => t.template.id === activeId) : null;

  return (
    <Box
      component="aside"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: LAYOUT_DIMENSIONS.SIDEBAR_WIDTH,
        minWidth: LAYOUT_DIMENSIONS.SIDEBAR_MIN_WIDTH,
        height: '100%',
        borderRight: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.default',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          flexShrink: 0,
          height: LAYOUT_DIMENSIONS.TOP_BAR_HEIGHT,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          px: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Typography
          variant="subtitle1"
          color="text.primary"
          sx={{fontWeight: 600,  mb: 0.5, lineHeight: 1.2 }}
        >
          Your Templates
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.2 }}>
          Select a template to edit or create a new one
        </Typography>
      </Box>

      {/* Templates List */}
      <Box
        sx={{
          flex: 1,
          overflow: 'hidden',
          position: 'relative',
          minHeight: 0,
        }}
      >
        <Box
          ref={templatesListRef}
          id="templates-sidebar-list"
          sx={{
            height: '100%',
            overflowY: 'scroll',
            overflowX: 'hidden',
            p: 2,
            pb: 0,
          }}
        >
          {isLoading ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} variant="rounded" sx={{height: 48,  borderRadius: 2 }} />
              ))}
            </Box>
          ) : templates.length === 0 ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                py: 4,
                textAlign: 'center',
              }}
            >
              <Typography variant="body2" color="text.secondary">
                No templates yet.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Create one below!
              </Typography>
            </Box>
          ) : (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
              measuring={{
                droppable: {
                  strategy: MeasuringStrategy.BeforeDragging,
                },
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1,
                  pb: 2,
                }}
              >
                {/* Pinned templates - sortable */}
                {pinnedTemplates.length > 0 && (
                  <SortableContext
                    items={pinnedTemplates.map(t => t.template.id)}
                    strategy={verticalListSortingStrategy}
                  >
                    {pinnedTemplates.map(template => (
                      <TemplateListItem
                        key={template.template.id}
                        template={template}
                        isSelected={selectedTemplateId === template.template.id}
                        onClick={() => onSelectTemplate(template)}
                        onPinClick={e => handlePinClick(e, template)}
                        isPinned={true}
                      />
                    ))}
                  </SortableContext>
                )}

                {/* Divider between pinned and unpinned templates */}
                {pinnedTemplates.length > 0 && unpinnedTemplates.length > 0 && (
                  <Divider
                    sx={{
                      my: 0.5,
                      mx: 1,
                      borderColor: 'divider',
                    }}
                  />
                )}

                {/* Unpinned templates - not sortable */}
                {unpinnedTemplates.map(template => (
                  <TemplateListItem
                    key={template.template.id}
                    template={template}
                    isSelected={selectedTemplateId === template.template.id}
                    onClick={() => onSelectTemplate(template)}
                    onPinClick={e => handlePinClick(e, template)}
                    isPinned={false}
                  />
                ))}
              </Box>

              {/* DragOverlay for smooth dragging experience */}
              <DragOverlay
                dropAnimation={{
                  duration: 200,
                  easing: 'cubic-bezier(0.18, 0.67, 0.6, 1.22)',
                }}
              >
                {activeTemplate && (
                  <Paper
                    elevation={8}
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      cursor: 'grabbing',
                      opacity: 0.9,
                      border: 2,
                      borderColor: 'primary.main',
                      bgcolor: 'background.paper',
                      minWidth: 200,
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {activeTemplate.displayName}
                    </Typography>
                  </Paper>
                )}
              </DragOverlay>
            </DndContext>
          )}
        </Box>

        {/* Bottom gradient indicator - fixed to bottom of container */}
        {showBottomGradient && (
          <Box
            sx={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              height: 32,
              pointerEvents: 'none',
              background: t =>
                `linear-gradient(to bottom, transparent 0%, ${t.palette.grey[100]} 100%)`,
              zIndex: 1,
            }}
          />
        )}
      </Box>

      {/* Actions Section */}
      <Box
        sx={{
          flexShrink: 0,
          p: 2,
          borderTop: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
        }}
      >
        {/* Entitlement Usage Display */}
        <EntitlementUsage
          capabilityName="template_creation"
          source="templates_sidebar"
          variant="compact"
          sx={{ mb: 2 }}
        />

        <Typography
          variant="caption"
          color="text.secondary"
          sx={{fontWeight: 600,
            display: 'block',
            mb: 1.5,
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
          }}
        >
          Create New
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {/* Upload Template Button */}
          <EntitlementDisabledTooltip capabilityName="template_creation">
            <Button
              variant="outlined"
              startIcon={<UploadIcon />}
              onClick={handleUploadClick}
              fullWidth
              sx={{
                justifyContent: 'flex-start',
                textTransform: 'none',
                fontWeight: 500,
                py: 1,
                borderColor: 'divider',
                color: 'text.primary',
                '&:hover': {
                  borderColor: 'primary.main',
                  bgcolor: 'action.hover',
                },
              }}
            >
              Upload Template
            </Button>
          </EntitlementDisabledTooltip>

          {/* Start from Scratch Button */}
          <EntitlementDisabledTooltip capabilityName="template_creation">
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={onCreateFromScratch}
              fullWidth
              sx={{
                justifyContent: 'flex-start',
                textTransform: 'none',
                fontWeight: 500,
                py: 1,
                borderColor: 'divider',
                color: 'text.primary',
                '&:hover': {
                  borderColor: 'primary.main',
                  bgcolor: 'action.hover',
                },
              }}
            >
              Start from Scratch
            </Button>
          </EntitlementDisabledTooltip>

          {/* Browse Library Button */}
          <Button
            variant="outlined"
            startIcon={<LibraryIcon />}
            onClick={onBrowseLibrary}
            fullWidth
            sx={{
              justifyContent: 'flex-start',
              textTransform: 'none',
              fontWeight: 500,
              py: 1,
              borderColor: 'divider',
              color: 'text.primary',
              '&:hover': {
                borderColor: 'primary.main',
                bgcolor: 'action.hover',
              },
            }}
          >
            Browse Library
          </Button>

          {/* Organization Templates Button - Only for Org Admins */}
          {isOrgAdmin && (
            <Button
              variant="outlined"
              startIcon={<OrgIcon />}
              onClick={onViewOrganizationTemplates}
              fullWidth
              sx={{
                justifyContent: 'flex-start',
                textTransform: 'none',
                fontWeight: 500,
                py: 1,
                borderColor: 'divider',
                color: 'text.primary',
                '&:hover': {
                  borderColor: 'primary.main',
                  bgcolor: 'action.hover',
                },
              }}
            >
              Organization Templates
            </Button>
          )}
        </Box>
      </Box>
    </Box>
  );
};
